import { TOOL_COUNT } from '../../content/toolCount';
/**
 * homeContent.js — the homepage's canon content layer (round-6 revamp, Aug 2026).
 *
 * Every user-visible string the new fold / demo tile / hire modal renders MUST
 * trace back to this module — never a hardcoded string in a component slot.
 * (Standing lesson: a hardcoded "Start free" once shipped in the sticky CTA
 * while the correct label sat unused in the aria-label. The port's copy
 * assertion greps rendered CTA strings against this file and fails on drift.)
 *
 * Copy provenance: locked with Nitish across the Aug 17-19 design sessions;
 * the decision record is the round-6 hub in Maeve's workspace
 * (work/home_round6/index.html). Do not re-word without an explicit decision.
 *
 * Notes that bind:
 *  - NO PRICE IN THE FOLD (Aug 17): the cost answer lives in the FAQ ("What is
 *    a shift?" + "How much is an AI employee?"), never in hero/mid-CTA copy.
 *  - Time claims: the hire film is 74 seconds, never "60". Signup-to-hire says
 *    "about a minute" until a timed fresh-account run lands under 60s.
 *  - The H1 is NOT here: the fold renders prod's existing H1 verbatim
 *    (static aria-label + RoleRotator), untouched by this revamp.
 */

export const HERO = {
  /* The locked sub-line (Aug 17 late eve, his final assembly). The control
     sentence gets its OWN line: on the natural wrap, "Anything" dangled alone
     and the promise read as an afterthought. Layout split only — the copy
     reads as one paragraph to anything that reads text rather than pixels. */
  subMain:
    'Hand over the messy, real-world problems. They think, research, build, and deliver. They own the outcome.',
  subLast: 'Anything risky waits for your yes.',

  /* The three trust bullets (locked Aug 17, his order). Bullet 1 is legal
     under the world-class-with-receipt rule ONLY because the dated benchmark
     pill sits on the same screen, directly above this list. Bullet 3's
     "60 seconds" claim is backed by CEL-1297 timed-run verification; fallback
     if the stopwatch ever disagrees: "about a minute". */
  trust: [
    'World-class reasoning powers every employee.',
    'Enterprise quality, none of the enterprise complexity.',
    'Working in 60 seconds, no tools to wire together.',
  ],

  /* CTA LABEL (Nitish, Aug 22; demo link REMOVED Aug 24). "Hire your AI
     employee" asked for the whole commitment in the first 5 seconds AND
     echoed the H1's verb; since the activation fork shipped, the fork does
     the qualifying one screen later, so the button's only job is opening the
     door. HISTORY of the demo ask: "Get a demo" modal (0 of 9 converted) →
     demoted to a "See the demo" link scrolling to the demo frame (Aug 22) →
     REMOVED entirely (Nitish, Aug 24): 7 days on prod showed 5 link clicks /
     3 sessions vs 36 clicks on the primary, while the demo frame itself drew
     56 sessions of direct touches. Visitors scroll to the demo; they don't
     click to it. The frame (id="home-demo") and its tracking are unchanged. */
  cta: 'Get started',
  support: 'Try it free. No credit card needed.',
};

/* The click-to-play film tile, directly after the demo frame. Same design the
   tile always had (thumbnail + copy + arrow), spanning the frame's width. */
export const VIDEO_TILE = {
  title: 'Watch one get hired',
  dur: '74s',
  blurb: 'The real product, start to finish.',
  poster: '/assets/cellcog/hire-journey-poster.jpg?v=7',
  src: '/assets/cellcog/hire-journey-web.mp4',
};


/* Mid-page CTA (lean pass): the $25 honesty line moved to the FAQ where the
   "What is a shift?" entry gives it the room it needs. */
export const MIDCTA = {
  title: 'A minute from now, your first employee is working.',
  sub: 'Try it free. No credit card needed.',
};

/* The hiring strip (Aug 25, ruled live with Nitish): header is TIMELESS — the
   data does the freshness, the copy never claims a window. Roles + employee
   first initials only; never counts, never customer identity. Decision record:
   Maeve, work/hiring_strip/PRD.md. */
export const HIRING_STRIP = {
  header: 'AI employees recently hired on CellCog',
};

/* The trust row between the Nora film and the FAQ (Nitish, Sep 16 2026, ruled
   live: TWO tiles and only two; the benchmark and the proof line already live
   elsewhere on the page). Every claim traces to org canon `security-claims`:
   dated text, Gmail + Google Analytics on our own client only, never
   "certified". `casa.badgeSrc` stays null until TAC Security's own badge file
   is in the repo (requested with the LOV PDF); the tile renders its label
   until then. Rendered by HomeTrustRow. Decision record: Maeve, work/trust_row/.
   Oct 14 2026 (Nitish): a third tile, The New York Times, now comes FIRST; its
   copy lives in content/nytFeature.js, not here. */
export const TRUST_ROW = {
  aria: 'Press and security',
  casa: {
    badgeSrc: null,
    badgeAlt: 'TAC Security ESOF Shield, CASA Tier 2',
    title: 'CASA Tier 2, independently assessed',
    line: 'Our application security was assessed by TAC Security, an App Defense Alliance authorized lab. Reassessed every year.',
  },
  gmail: {
    title: 'Verified for Gmail',
    line: 'Google reviewed how CellCog connects to your Gmail and Google Analytics and approved it. September 2026.',
  },
};

/* Section 01's six promise cards (replaced the check-bullet list, Aug 28 2026
   — Nitish's live pick: Option A quiet cards, 3x2 desktop / 2 across phone;
   decision record: Maeve, work/home_sixbox/). Wording is the locked Aug-27
   card set; "Always on" is HIS line (dated ruling Aug 27) — do not re-word
   without an explicit decision. Oct 12 2026 (Nitish): card 1 title gains
   ", 24/7" (body unchanged), card 3 title adds the phone number, card 5 body
   adds the Spend line; card 4 body names Claude Code, Codex and Cursor (subagents, Oct 12). Rendered by HomeFeatureCards; icon keys map
   to the hand-drawn marks there. */
export const AIE_CARDS = [
  {
    icon: 'clock',
    title: 'Always on, 24/7',
    body: 'Works on a schedule, or the moment you ask. Nights, weekends, right now.',
  },
  {
    icon: 'memory',
    title: 'Persistent memory',
    body: 'Month six remembers day one. Decisions, preferences, context. Nothing resets.',
  },
  {
    icon: 'inbox',
    title: 'Their own inbox, email and phone number',
    body: 'With their own task board too, and a computer of their own.',
  },
  {
    icon: 'tools',
    title: 'Works in the tools you already pay for',
    body: 'Your laptop, your browser, ' + TOOL_COUNT + ' tools, and your Claude Code, Codex or Cursor.',
  },
  {
    icon: 'control',
    title: 'You stay in control',
    body: 'Anything risky waits for your yes. They buy only what you approve.',
  },
  {
    icon: 'report',
    title: 'Tells you what they did',
    body: 'A written report at the end of every working day.',
  },
];
